/* component-video-pastilles.js — Slice 32 Video Pastilles, P2 + P3.
 *
 * One Custom Element <video-pastilles> per block instance. Responsibilities:
 * (P2) intercept pastille clicks in the CAPTURE phase and open a shared popup
 * <dialog> (the reused video-fullscreen.liquid shell), re-sourced per pastille
 * via the engine's video-player:activate event; play/close/Esc/backdrop/teardown
 * + iOS-safe body scroll-lock, adapted from the carousel's initFullscreen
 * (sections/section-video-carousel.js) and scoped to THIS instance's dialog.
 * (P3) auto-scroll: overflow detection (updateOverflow) toggling .can-left/
 * .can-right on the viewport, arrow-click scroll (~70%, reduced-motion aware),
 * and re-run on scroll (rAF-throttled) + ResizeObserver. The arrow buttons are
 * siblings of .pvp__scroller, so the P2 capture listener never sees them.
 * (P4) Stacked See/Reduce (D-11): the See button (rendered inside the scroller
 * for stacked only) toggles data-stack-open on the root → CSS un-overlaps the
 * pile into a scrollable strip; the toggle re-runs overflow (immediate + on the
 * margin transitionend). Chips + Stacked are pure parent CSS (no JS branch).
 *
 * Why capture: the engine binds inline-play as a BUBBLE click on the inner
 * .video-player (component-video-player.js:158). A capture-phase listener on
 * the ancestor .pvp__scroller fires first; stopPropagation() there halts the
 * event before it reaches the engine → no inline <video> is built, no flash
 * (cleaner than the carousel, which lets inline start then tears it down).
 * Keyboard Enter/Space on the engine's focusable <button class="video-player__play">
 * dispatches a click that still runs the capture phase → caught here. So no
 * role/tabindex on the .pvp__item <div> (D-16 — the engine's aria-labelled
 * play button is the accessible trigger).
 *
 * Rules: J2 (Custom Element, upgrade-driven — no DOMContentLoaded gate),
 * J7 (disconnectedCallback removes every listener added), J12 (IIFE dedup +
 * idempotent define). Multiple instances per page each own their own dialog
 * via this.querySelector; the engine binds the dialog's [data-video-player]
 * on its own init pass.
 */
(function () {
  'use strict';

  if (window.__BLOCKDEDUP_VIDEO_PASTILLES__) {
    return;
  }
  window.__BLOCKDEDUP_VIDEO_PASTILLES__ = true;

  class VideoPastilles extends HTMLElement {
    connectedCallback() {
      if (this.__pvpInit) {
        return;
      }
      this.__pvpInit = true;

      this.scroller = this.querySelector('.pvp__scroller');
      this.viewport = this.querySelector('.pvp__viewport');
      this.prevBtn = this.querySelector('.pvp__arrow--prev');
      this.nextBtn = this.querySelector('.pvp__arrow--next');
      this.dialog = this.querySelector('.video-fullscreen');
      this.player = this.dialog ? this.dialog.querySelector('.video-fullscreen__player') : null;
      this.closeBtn = this.dialog ? this.dialog.querySelector('[data-video-fullscreen-close]') : null;
      if (!this.scroller || !this.dialog || !this.player) {
        return;
      }

      this._savedScrollY = 0;
      this._fsOpen = false;

      /* Bound handlers stored on the instance so disconnectedCallback can
         remove the exact same references (J7). */
      this._onCapture = this._onCaptureClick.bind(this);
      this._onCloseBtn = this._onCloseBtnClick.bind(this);
      this._onBackdrop = this._onBackdropClick.bind(this);
      this._onDlgClose = this._onDialogClose.bind(this);

      this.scroller.addEventListener('click', this._onCapture, true); /* capture = true */
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this._onCloseBtn);
      }
      this.dialog.addEventListener('click', this._onBackdrop);
      this.dialog.addEventListener('close', this._onDlgClose);

      /* --- P3 §4.4 auto-scroll: overflow detection + arrows. Independent of the
         modal wiring above; only runs when the viewport exists. --- */
      if (this.viewport) {
        this._scrollRAF = 0;
        this._onScroll = this._onScrollHandler.bind(this);
        this._onPrev = this._scrollByDir.bind(this, -1);
        this._onNext = this._scrollByDir.bind(this, 1);

        this.scroller.addEventListener('scroll', this._onScroll, { passive: true });
        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', this._onPrev);
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', this._onNext);
        }

        /* RO on the scroller catches viewport-width + item-size/gap changes; a
           video-count change re-renders the block → connectedCallback re-runs the
           initial eval below. updateOverflow reads scrollWidth live, so any RO
           callback re-evaluates overflow correctly. */
        if ('ResizeObserver' in window) {
          this._resizeObserver = new ResizeObserver(this.updateOverflow.bind(this));
          this._resizeObserver.observe(this.scroller);
        }

        /* Initial eval after first layout. */
        requestAnimationFrame(this.updateOverflow.bind(this));
      }

      /* --- P4 D-11 See/Reduce: present only for the Stacked structure. Toggles
         data-stack-open on the root → CSS collapses/expands the pile; re-runs
         overflow so the expanded strip's arrows/fades update. --- */
      this.seeBtn = this.querySelector('.pvp__see');
      if (this.seeBtn) {
        this._onSee = this._onSeeToggle.bind(this);
        this.seeBtn.addEventListener('click', this._onSee);

        /* O-2: the collapse->expand margin transition changes scrollWidth over
           ~200ms, so the immediate updateOverflow() below can read the stale
           (collapsed) width. Re-measure on transitionend (bubbles from the pile
           children); cheap + idempotent. Filtered to margin-inline-start. */
        this._onStackTransition = this._onStackTransitionEnd.bind(this);
        this.scroller.addEventListener('transitionend', this._onStackTransition);
      }
    }

    disconnectedCallback() {
      if (this.scroller) {
        this.scroller.removeEventListener('click', this._onCapture, true);
      }
      if (this.closeBtn) {
        this.closeBtn.removeEventListener('click', this._onCloseBtn);
      }
      if (this.dialog) {
        this.dialog.removeEventListener('click', this._onBackdrop);
        this.dialog.removeEventListener('close', this._onDlgClose);
      }
      /* P3 auto-scroll teardown (J7): every listener + the observer + the pending rAF. */
      if (this.scroller && this._onScroll) {
        this.scroller.removeEventListener('scroll', this._onScroll);
      }
      if (this.prevBtn && this._onPrev) {
        this.prevBtn.removeEventListener('click', this._onPrev);
      }
      if (this.nextBtn && this._onNext) {
        this.nextBtn.removeEventListener('click', this._onNext);
      }
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
      }
      if (this._scrollRAF) {
        cancelAnimationFrame(this._scrollRAF);
        this._scrollRAF = 0;
      }
      /* P4 See/Reduce teardown (J7). */
      if (this.seeBtn && this._onSee) {
        this.seeBtn.removeEventListener('click', this._onSee);
      }
      if (this.scroller && this._onStackTransition) {
        this.scroller.removeEventListener('transitionend', this._onStackTransition);
      }
    }

    /* See/Reduce click: flip data-stack-open on the root + aria-expanded, then
       re-measure overflow (immediate pass; transitionend does the final pass). */
    _onSeeToggle() {
      var next = this.getAttribute('data-stack-open') === 'true' ? 'false' : 'true';
      this.setAttribute('data-stack-open', next);
      this.seeBtn.setAttribute('aria-expanded', next);
      this.updateOverflow();
    }

    /* Final-layout overflow pass once the pile's margin transition ends (O-2). */
    _onStackTransitionEnd(event) {
      if (event.propertyName === 'margin-inline-start') {
        this.updateOverflow();
      }
    }

    /* Toggle .can-left/.can-right on the viewport → CSS shows/hides each arrow + fade. */
    updateOverflow() {
      if (!this.scroller || !this.viewport) {
        return;
      }
      var s = this.scroller;
      var left = s.scrollLeft > 1;
      var right = s.scrollLeft + s.clientWidth < s.scrollWidth - 1;
      this.viewport.classList.toggle('can-left', left);
      this.viewport.classList.toggle('can-right', right);
    }

    /* Arrow click: scroll ~70% of the viewport (reduced-motion → instant). */
    _scrollByDir(dir) {
      if (!this.scroller) {
        return;
      }
      var s = this.scroller;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      s.scrollBy({ left: s.clientWidth * 0.7 * dir, behavior: reduce ? 'auto' : 'smooth' });
    }

    /* rAF-throttled scroll → updateOverflow. */
    _onScrollHandler() {
      if (this._scrollRAF) {
        return;
      }
      this._scrollRAF = requestAnimationFrame(function () {
        this._scrollRAF = 0;
        this.updateOverflow();
      }.bind(this));
    }

    _onCaptureClick(event) {
      var item = (event.target && event.target.closest) ? event.target.closest('.pvp__item') : null;
      if (!item || !this.contains(item)) {
        return;
      }
      /* Pre-empt the engine's bubble-phase inline-play click (no flash, D-16). */
      event.stopPropagation();
      event.preventDefault();
      this._openModal(item);
    }

    _openModal(item) {
      if (this._fsOpen) {
        return;
      }

      var srcEl = item.querySelector('.video-player__sources');
      var sources = [];
      try {
        sources = JSON.parse(srcEl && srcEl.textContent ? srcEl.textContent : '[]');
      } catch (err) {
        sources = [];
      }
      /* No video assigned → don't open an empty modal. */
      if (!Array.isArray(sources) || sources.length === 0) {
        return;
      }

      var aspect = item.getAttribute('data-video-aspect');
      if (!aspect) {
        aspect = '9 / 16';
      }
      this.player.style.setProperty('--vfs-aspect', aspect);

      try {
        this.dialog.showModal();
      } catch (err) {
        this.dialog.setAttribute('open', '');
      }
      this._lockScroll();
      this._fsOpen = true;

      /* Re-source the shared dialog player via the engine's opts.sources
         override (DEV-S13-D-ENGINE-OPTS-SOURCES). Unmuted — we're inside a
         user-gesture click path. */
      this.player.dispatchEvent(new CustomEvent('video-player:activate', {
        detail: { muted: false, controls: true, sources: sources }
      }));
    }

    _onCloseBtnClick() {
      this._close();
    }

    _onBackdropClick(event) {
      /* Fires on the dialog itself; inner clicks (video controls, close button)
         don't bubble up with target === dialog. */
      if (event.target === this.dialog) {
        this._close();
      }
    }

    _close() {
      if (!this._fsOpen) {
        return;
      }
      try {
        this.dialog.close();
      } catch (err) {
        this.dialog.removeAttribute('open');
      }
    }

    _onDialogClose() {
      /* SINGLE cleanup path — Esc, X, backdrop, and programmatic close all land
         here (native <dialog> fires 'close' for every dismissal). */
      if (!this._fsOpen) {
        return;
      }
      try {
        this.player.dispatchEvent(new CustomEvent('video-player:teardown'));
      } catch (err) {
        /* benign */
      }
      this._unlockScroll();
      this._fsOpen = false;
    }

    _lockScroll() {
      /* iOS-safe position:fixed lock — mirrors the carousel (lines 194-208). */
      this._savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.insetBlockStart = '-' + this._savedScrollY + 'px';
      document.body.style.inlineSize = '100%';
      document.body.classList.add('video-fullscreen-open');
    }

    _unlockScroll() {
      document.body.style.position = '';
      document.body.style.insetBlockStart = '';
      document.body.style.inlineSize = '';
      document.body.classList.remove('video-fullscreen-open');
      window.scrollTo(0, this._savedScrollY);
    }
  }

  if (!customElements.get('video-pastilles')) {
    customElements.define('video-pastilles', VideoPastilles);
  }
})();
